/**
 * Utility functions for handling API responses and data extraction
 */

/**
 * Extracts data from various API response formats
 * @param {Object} response - The API response object
 * @param {string} dataPath - Optional dot-separated path to extract data from
 * @param {*} defaultValue - Default value if data not found
 * @returns {*} The extracted data or defaultValue
 */
export const extractDataFromResponse = (response, dataPath = null, defaultValue = null) => {
  if (!response) return defaultValue;
  
  // Log the response for debugging
  console.log('Extracting data from response:', response, 'at path:', dataPath);

  // If a specific data path is provided, try to extract data using it
  if (dataPath) {
    const parts = dataPath.split('.');
    let current = response;
    
    for (const part of parts) {
      if (!current || typeof current !== 'object') {
        console.warn(`Failed to extract data at path "${dataPath}" - stopped at "${part}"`, response);
        return defaultValue;
      }
      current = current[part];
    }
    
    return current === undefined ? defaultValue : current;
  }
  
  // Try known response structures
  if (response.data?.data?.config) {
    return response.data.data.config;
  }
  
  if (response.data?.config) {
    return response.data.config;
  }
  
  if (response.data) {
    return response.data;
  }
  
  if (response.config) {
    return response.config;
  }
  
  // Just return the response if it's already the data we need
  return response || defaultValue;
};

/**
 * Sets default values for missing properties in an object
 * @param {Object} data - The object to check
 * @param {Object} defaults - Default values for properties
 * @returns {Object} Object with defaults applied
 */
export const applyDefaults = (data, defaults) => {
  if (!data || typeof data !== 'object') return { ...defaults };
  
  const result = { ...defaults };
  
  // Only use properties from data that exist in defaults
  Object.keys(defaults).forEach(key => {
    if (data[key] !== undefined) {
      result[key] = data[key];
    }
  });
  
  return result;
};

export default {
  extractDataFromResponse,
  applyDefaults
}; 